import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Row, Col, message, Select, Switch, Divider, Tag } from 'antd';
import CloudinaryUploader from '../../util/CloudinaryUploader';
import { PostDataAuth } from './../../apiService/PostData';
import { PutDataAuth } from './../../apiService/PutData';
import { apis } from './../../properties';

const { TextArea } = Input;
const { Option } = Select;

const SECTION_KEY_OPTIONS = [
    { label: 'Hero Banner', value: 'hero-banner' },
    { label: 'Meet Harriettes', value: 'meet-harriettes' },
    { label: 'We do runs', value: 'we-do-runs' },
    { label: 'We go on trips', value: 'we-go-on-trips' },
    { label: 'We drink beer', value: 'we-drink-beer' },
    { label: 'Join Us', value: 'join-us' },
    { label: 'About Us', value: 'about-us' },
];

const AddContentModal = ({ visible, onCancel, onSubmitSuccess, initialValues }) => {
    const [form] = Form.useForm();
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // New state for Tags
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');

    const isEditMode = !!initialValues;

    const [enabledFields, setEnabledFields] = useState({
        subtitle: false,
        description: false,
        media: false,
    });

    useEffect(() => {
        if (visible) {
            if (isEditMode) {
                form.setFieldsValue(initialValues);
                setMediaList(initialValues.media || []);
                setTags(initialValues.meta_tags || []); // Load existing tags
                setEnabledFields({
                    subtitle: !!initialValues.has_subtitle,
                    description: !!initialValues.has_description,
                    media: !!initialValues.has_media
                });
            } else {
                form.resetFields();
                setMediaList([]);
                setTags([]);
                setEnabledFields({ subtitle: false, description: false, media: false });
            }
        }
    }, [visible, initialValues, form, isEditMode]);

    // Tag Handlers
    const handleAddTag = () => {
        if (tagInput && !tags.includes(tagInput) && tags.length < 5) {
            setTags([...tags, tagInput]);
            setTagInput('');
        } else if (tags.length >= 5) {
            message.warning("Maximum of 5 tags allowed.");
        }
    };

    const handleRemoveTag = (removedTag) => {
        setTags(tags.filter((tag) => tag !== removedTag));
    };

    const handleToggle = (field, checked) => {
        setEnabledFields((prev) => ({ ...prev, [field]: checked }));
        if (!checked) {
            if (field === 'media') {
                setMediaList([]);
            } else {
                form.setFieldsValue({ [`section_${field}`]: '' });
            }
        }
    };

    const handleFinish = async (values) => {
        setLoading(true);
        try {
            const payload = { 
                ...values,
                meta_tags: tags, // Add tags to payload
                has_subtitle: enabledFields.subtitle,
                has_description: enabledFields.description,
                has_media: enabledFields.media
            };

            let sectionId = initialValues?._id;

            if (!isEditMode) {
                const response = await PostDataAuth(apis.SECTION_CONTENT, payload);
                sectionId = response?.data?._id || response?._id;
            } else {
                await PutDataAuth(`${apis.SECTION_CONTENT}/${sectionId}`, payload);
            }

            if (sectionId && enabledFields.media) {
                await PostDataAuth(`${apis.SECTION_CONTENT}/${sectionId}/section-media`, mediaList);
            }

            message.success(`Section ${isEditMode ? 'updated' : 'created'} successfully!`);
            form.resetFields();
            setTags([]);
            onSubmitSuccess();
            onCancel();
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to save content.");
        } finally {
            setLoading(false);
        }
    };

    const ToggleRow = ({ label, field, checked }) => (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '10px 0',
            borderBottom: '1px solid #f0f0f0' 
        }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>{label}</span>
            <Switch 
                disabled={isEditMode} 
                checked={checked} 
                onChange={(c) => handleToggle(field, c)} 
            />
        </div>
    );

    return (
        <Modal
            title={isEditMode ? "Edit Section Content" : "Create New Section"}
            open={visible}
            onCancel={onCancel}
            width={800}
            destroyOnClose
            maskClosable={false}
            footer={[
                <Button key="back" onClick={onCancel} disabled={loading}>Cancel</Button>,
                <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
                    {isEditMode ? "Update Content" : "Create Content"}
                </Button>
            ]}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Section Key" name="section_key" rules={[{ required: true }]}>
                            <Select placeholder="Choose a section" disabled={isEditMode}>
                                {SECTION_KEY_OPTIONS.map(opt => (
                                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Section Title" name="section_title" rules={[{ required: true }]}>
                            <Input placeholder="Main heading" />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Meta Tags Input */}
                <Form.Item label="Meta Tags (Max 5)">
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <Input 
                            value={tagInput} 
                            placeholder="Add a tag..." 
                            onChange={(e) => setTagInput(e.target.value)}
                            onPressEnter={(e) => { e.preventDefault(); handleAddTag(); }}
                            disabled={tags.length >= 5}
                        />
                        <Button onClick={handleAddTag} disabled={tags.length >= 5}>Add</Button>
                    </div>
                    <div>
                        {tags.map((tag) => (
                            <Tag key={tag} closable onClose={() => handleRemoveTag(tag)}>
                                {tag}
                            </Tag>
                        ))}
                    </div>
                </Form.Item>

                {!isEditMode && (
                    <>
                        <Divider orientation="left" plain>Configuration Toggles</Divider>
                        <div style={{ marginBottom: '24px', backgroundColor: '#fafafa', padding: '0 16px', borderRadius: '8px' }}>
                            <ToggleRow label="Subtitle" field="subtitle" checked={enabledFields.subtitle} />
                            <ToggleRow label="Description" field="description" checked={enabledFields.description} />
                            <ToggleRow label="Media Assets" field="media" checked={enabledFields.media} />
                        </div>
                    </>
                )}

                {enabledFields.subtitle && (
                    <Form.Item name="section_subtitle" label="Subtitle">
                        <Input placeholder="Subheading text" />
                    </Form.Item>
                )}

                {enabledFields.description && (
                    <Form.Item name="section_description" label="Description">
                        <TextArea rows={3} placeholder="Section details..." />
                    </Form.Item>
                )}

                {enabledFields.media && (
                    <div style={{ padding: '15px', background: '#f5f5f5', borderRadius: '8px', marginTop: '10px' }}>
                        <p style={{ fontWeight: 600, marginBottom: 10 }}>Media Assets</p>
                        <Form.Item noStyle shouldUpdate={(prev, current) => prev.section_key !== current.section_key}>
                            {({ getFieldValue }) => (
                                <CloudinaryUploader
                                    onUpdate={setMediaList}
                                    existingMedia={mediaList}
                                    sectionKey={getFieldValue('section_key')}
                                />
                            )}
                        </Form.Item>
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default AddContentModal;